import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { ORIGIN_META } from "../../lib/origin.jsx";
import { formatNumber, cn } from "../../lib/utils";

function Node({ label, units, depth, children, origin, testid }) {
  const [open, setOpen] = useState(false);
  const leaf = !children;
  return (
    <div data-testid={testid}>
      <button onClick={() => !leaf && setOpen(o => !o)} data-testid={`${testid}-toggle`}
              className={cn("w-full flex items-center gap-1.5 py-1 text-left text-[11px] rounded-sm", !leaf && "hover:bg-surface-2/70")}
              style={{ paddingLeft: depth * 14 }}>
        {leaf ? <span className="w-[11px]" /> : <ChevronRight size={11} className={cn("text-muted transition-transform", open && "rotate-90")} />}
        <span className={cn("truncate", depth === 0 && "font-semibold")}>{label}</span>
        {origin && origin !== "synthetic" && <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", ORIGIN_META[origin].dot)} title={`${ORIGIN_META[origin].label} — uploaded install base`} />}
        <span className="ml-auto font-mono-tabular text-muted">{formatNumber(units)}</span>
      </button>
      {open && children}
    </div>
  );
}

// Region → province → city/municipality tree of compatible installed aircon units.
export default function InstallBaseTree({ ib }) {
  if (!ib.models.length) {
    return <div className="text-[11px] text-muted" data-testid="install-tree-empty">Not tied to an aircon model (consumable or bulk item) — no install-base driver.</div>;
  }
  return (
    <div data-testid="install-tree">
      <div className="text-[10.5px] text-muted mb-2 leading-snug">
        Compatible models: <span className="text-fg">{ib.models.map(m => m.label).join(", ")}</span>.{" "}
        {ib.rate != null && <>{formatNumber(ib.total_units)} units × {(ib.rate * 100).toFixed(0)}% replacement rate shared across compatible SKUs ≈ <strong className="text-fg font-mono-tabular">{formatNumber(ib.replacement_per_year)}</strong> replacements / year.</>}
      </div>
      <div className="max-h-[200px] overflow-y-auto pr-1">
        {ib.regions.map(reg => (
          <Node key={reg.region} label={reg.region} units={reg.units} depth={0} testid={`install-region-${reg.region.replace(/\s+/g, "-")}`}>
            {reg.provinces.map(p => (
              <Node key={p.province} label={p.province} units={p.units} depth={1} testid={`install-province-${p.province.replace(/\s+/g, "-")}`}>
                {p.cities.map(c => (
                  <Node key={c.city} depth={2} units={c.units} origin={c.origin} testid={`install-city-${c.city.replace(/\s+/g, "-")}`}
                        label={`${c.city} · ${Object.entries(c.models).map(([m, u]) => `${m.split(" ").slice(0, 2).join(" ")} ${formatNumber(u)}`).join(" · ")}`} />
                ))}
              </Node>
            ))}
          </Node>
        ))}
      </div>
    </div>
  );
}
